import { createFileRoute } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { ATS_INTERACTIVE_STAGES, ATS_STAGE_LABELS, atsEffectivePipeline } from "@alm/domain";
import { PageHeader } from "@/components/app/shell/page-header";
import { ReadFailed } from "@/components/app/lists/read-failed";
import { Skeleton } from "@/components/ui/skeleton";
import { useFacility } from "@/lib/facility/facility";
import { orpc } from "@/lib/orpc";
import { useEveryApplication } from "./-components/use-every-application";
import { HiringFunnel } from "./-components/hiring-funnel";
import { PipelineApplications } from "./-components/pipeline-applications";
import { PipelineAttention } from "./-components/pipeline-attention";
import { RecentActivity } from "./-components/recent-activity";

export const Route = createFileRoute("/_authenticated/_workforce/hiring/dashboard")({
  component: HiringDashboard,
});

const OUTCOMES = [
  { stage: "hired", label: "hired" },
  { stage: "rejected", label: "not taken forward" },
  { stage: "converted", label: "now employees" },
];

const stageLabel = (stage: string) => ATS_STAGE_LABELS[stage] ?? stage;

/* Decided and done with: a rejected or converted candidate is not waiting on
   anybody, however long ago their stage last moved. */
const CLOSED = new Set(["rejected", "converted"]);

/** Where hiring stands: how many people sit at each step of the pipeline, which of them are waiting
 * on somebody here, and who has been waiting longest. */
function HiringDashboard() {
  const { facilityId } = useFacility();

  const overview = useQuery(orpc.workforce.hiring.overview.queryOptions({ input: { facilityId } }));
  const applications = useEveryApplication({ facilityId });
  const activity = useQuery(
    orpc.workforce.hiring.recentActivity.queryOptions({ input: { facilityId } }),
  );

  const byStage = overview.data?.byStage ?? [];
  const counts = new Map<string, number>(byStage.map((row) => [row.stage, row.total]));
  const countOf = (key: string) => counts.get(key) ?? 0;
  const total = byStage.reduce((sum, row) => sum + row.total, 0);
  const live = byStage.reduce((sum, row) => (CLOSED.has(row.stage) ? sum : sum + row.total), 0);
  const openJobs = overview.data?.openJobs ?? 0;
  const review = countOf("review");

  /* The funnel must be the widest pipeline any job could run, or people in headshot, video or
   * signatures stages vanish from the bars. Stages no job uses stay hidden. */
  const interactive = new Set<string>(ATS_INTERACTIVE_STAGES);
  const funnel = atsEffectivePipeline(ATS_INTERACTIVE_STAGES, true)
    .filter((key) => key !== "hired" && (!interactive.has(key) || countOf(key) > 0))
    .map((key) => ({ stage: key, label: stageLabel(key), total: countOf(key) }));

  const outcomes = OUTCOMES.filter(
    (outcome) => outcome.stage !== "converted" || countOf(outcome.stage) > 0,
  ).map((outcome) => ({ ...outcome, total: countOf(outcome.stage) }));

  const waiting = (applications.data?.rows ?? [])
    .filter((row) => !CLOSED.has(row.currentStage))
    .toSorted((left, right) => right.daysInStage - left.daysInStage)
    .slice(0, 10);

  return (
    <>
      <PageHeader
        title="Pipeline"
        meta={
          overview.isPending
            ? "Loading…"
            : overview.isError
              ? undefined
              : total === 0
                ? "Nobody has applied yet. Postings taking applications will show their candidates here."
                : `${live} ${live === 1 ? "person" : "people"} in the pipeline, ${openJobs} ${openJobs === 1 ? "job" : "jobs"} taking applications`
        }
      />

      {overview.isPending ? (
        <Skeleton className="h-28 w-full" />
      ) : overview.isError ? (
        <ReadFailed
          message="Could not load the pipeline counts."
          error={overview.error}
          retrying={overview.isFetching}
          onRetry={() => void overview.refetch()}
        />
      ) : (
        <>
          <PipelineAttention review={review} hired={countOf("hired")} />
          <HiringFunnel
            stages={funnel}
            outcomes={outcomes}
            emphasis={review > 0 ? "review" : null}
          />
        </>
      )}

      {applications.isPending ? (
        <Skeleton className="h-64 w-full" />
      ) : applications.isError ? (
        <ReadFailed
          message="Could not load the candidates waiting longest."
          error={applications.error}
          retrying={applications.isFetching}
          onRetry={() => void applications.refetch()}
        />
      ) : (
        <PipelineApplications title="Waiting longest" rows={waiting} />
      )}

      {activity.isPending ? (
        <Skeleton className="h-48 w-full" />
      ) : activity.isError ? (
        <ReadFailed
          message="Could not load recent hiring activity."
          error={activity.error}
          retrying={activity.isFetching}
          onRetry={() => void activity.refetch()}
        />
      ) : (
        <RecentActivity rows={activity.data ?? []} />
      )}
    </>
  );
}
